import {
	calculateQualificationScore,
	countQualifications,
	isAffirmativeQualification,
	OBSERVER_SCORE_MAX,
	QUALIFICATION_KEYS,
	type QualificationAnswers,
} from '@/constants/agent-score';

import {
	type IObserverApplicationDetail,
	type IObserverApplicationListParams,
	type IObserverApplicationSummary,
	type IObserverCoverageLevel,
	type IObserverCoverageSummary,
	type IObserverEntityCoverage,
	type IObserverLedgerRow,
	type IObserverStatusHistory,
	OBSERVER_DIVISION_TO_RESULT,
	OBSERVER_DIVISION_TYPES,
	ObserverApplicationStatus,
	type ObserverApplicationStatusId,
	type ObserverDivisionType,
	type ObserverQualificationKey,
} from './management-types';

export const OBSERVER_TYPES: { value: number; label: string }[] = [
	{ value: 1, label: 'International' },
	{ value: 2, label: 'Domestic' },
	{ value: 3, label: 'Media Organization' },
];

export const observerTypeLabel = (type?: number | string | null): string => {
	const num = Number(type);
	return OBSERVER_TYPES.find((item) => item.value === num)?.label ?? '--';
};

export const cleanObserverField = (value?: string | number | null): string => {
	if (value == null) return '';
	const str = `${value}`.trim();
	return str && str.toLowerCase() !== 'null' && str.toLowerCase() !== 'undefined' ? str : '';
};

export type ObserverStatusTone =
	| 'received'
	| 'offer'
	| 'reserved'
	| 'waitlisted'
	| 'expired'
	| 'confirmed'
	| 'approved'
	| 'rejected'
	| 'unknown';

/**
 * One case per status, and an honest "Unknown" for anything else. The old default was
 * "Application Received", so Reserved (7) and Waitlisted (8) rendered as brand-new
 * applications the moment the backend started sending them.
 */
export const observerVolunteerStatus = (status?: number | null): { label: string; tone: ObserverStatusTone } => {
	switch (status) {
		case ObserverApplicationStatus.PENDING:
			return { label: 'Application Received', tone: 'received' };
		case ObserverApplicationStatus.APPROVED:
			return { label: 'Approved', tone: 'approved' };
		case ObserverApplicationStatus.REJECTED:
			return { label: 'Rejected', tone: 'rejected' };
		case ObserverApplicationStatus.OFFER_SENT:
			return { label: 'Offer Sent', tone: 'offer' };
		case ObserverApplicationStatus.OFFER_EXPIRED:
			return { label: 'Offer Expired', tone: 'expired' };
		case ObserverApplicationStatus.CONFIRMED:
			return { label: 'Confirmed Observer', tone: 'confirmed' };
		case ObserverApplicationStatus.RESERVED:
			return { label: 'Reserved', tone: 'reserved' };
		// The ticket's "Pending": queued behind the offer holder and the Reserved applicant.
		case ObserverApplicationStatus.WAITLISTED:
			return { label: 'Pending', tone: 'waitlisted' };
		default:
			return { label: 'Unknown Status', tone: 'unknown' };
	}
};

export const isPendingObserverVolunteer = (status?: number | null): boolean => status === ObserverApplicationStatus.PENDING;

/**
 * Whether `/ObserverApplication/{id}/approve` is a live destination for this application.
 *
 * Only Application Received (1). The server answers 400 for every other status — Reserved and
 * Waitlisted included: they move up through the expiry sweep, never through Approve. This is
 * the status half of the gate; `observerApproveBlockedReason` adds the per-entity half.
 */
export const canApproveObserverApplication = (status?: number | null): boolean => status === ObserverApplicationStatus.PENDING;

export const canRejectObserverApplication = (status?: number | null): boolean => status !== ObserverApplicationStatus.REJECTED;

export const isActiveObserverOffer = (status?: number | null): boolean => status === ObserverApplicationStatus.OFFER_SENT;
export const isConfirmedObserver = (status?: number | null): boolean => status === ObserverApplicationStatus.CONFIRMED;

/** An offer is out (4) or someone accepted one (6) — either way the entity's single place is taken. */
export const holdsObserverPlace = (status?: number | null): boolean =>
	isActiveObserverOffer(status) || isConfirmedObserver(status);

const entityKey = (code?: string | null): string => cleanObserverField(code).toLowerCase();

/**
 * Entity codes, normalised, where someone in `applications` holds the offer or is confirmed.
 *
 * Only as complete as what it is given: a page of a paged list, or a status-filtered one, can
 * miss the holder. Callers that know more (the coverage row's `hasActiveOffer` / `isAssigned`)
 * pass it to `observerApproveBlockedReason` as well, and the server's 400 stays the backstop.
 */
export const observerEntitiesWithPlaceTaken = (
	applications: Pick<IObserverApplicationSummary, 'entityCode' | 'status'>[]
): Set<string> => new Set(applications.filter((a) => holdsObserverPlace(a.status)).map((a) => entityKey(a.entityCode)));

/**
 * Why Approve is closed for an applicant whose OWN status allows it, or '' when it is open.
 *
 * api.core allows one live offer per entity: while someone for the entity is at Offer Sent or
 * Confirmed, approving anyone else answers 400 — including applications that arrived after the
 * offer went out, which stay at Application Received.
 */
export const observerApproveBlockedReason = (
	application: Pick<IObserverApplicationSummary, 'entityCode' | 'status'>,
	placeTaken: { entityCodes?: Set<string>; offerOut?: boolean; confirmed?: boolean } = {}
): string => {
	if (!canApproveObserverApplication(application.status)) return '';
	if (placeTaken.confirmed) return 'This entity already has a confirmed observer.';
	if (placeTaken.offerOut) return 'Another applicant holds the offer for this entity. Approve opens again if it expires.';
	if (placeTaken.entityCodes?.has(entityKey(application.entityCode)))
		return 'Another applicant holds the offer or is confirmed for this entity.';
	return '';
};

/**
 * A Reserved applicant with nobody holding the place — the state the backend says needs an admin.
 *
 * The sweep skips a promotion when the admin who paid for the lapsed offer has no usable wallet
 * or credit, and re-running it later does not retry. Rejecting the offer holder leaves the same
 * gap. Only meaningful over an entity's FULL, unfiltered applicant list.
 */
export const isObserverReserveStranded = (applications: Pick<IObserverApplicationSummary, 'status'>[]): boolean =>
	applications.some((a) => a.status === ObserverApplicationStatus.RESERVED) &&
	!applications.some((a) => holdsObserverPlace(a.status));

export const formatObserverDate = (value?: string | null): string => {
	const cleaned = cleanObserverField(value);
	if (!cleaned) return '--';
	const date = new Date(cleaned);
	return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-NG');
};

/**
 * Funnel order — live workflow first, then terminal states — the same reading order as the agent
 * side's `VOLUNTEER_STATUS_ORDER`. Labels come from `observerVolunteerStatus`, so "Pending" here
 * is the waitlisted 8 and never the received 1. Approved (2) stays filterable for legacy rows.
 */
const OBSERVER_STATUS_FILTER_ORDER: ObserverApplicationStatusId[] = [1, 4, 7, 8, 6, 5, 3, 2];

export const OBSERVER_STATUS_FILTERS: { value: '' | `${ObserverApplicationStatusId}`; label: string }[] = [
	{ value: '', label: 'All' },
	...OBSERVER_STATUS_FILTER_ORDER.map((id) => ({ value: `${id}` as const, label: observerVolunteerStatus(id).label })),
];
/**
 * An observer application's qualification score, computed from the application itself.
 *
 * Five self-declared readiness facts — smart phone with internet, availability all day,
 * party agent experience, native language speaker, power bank — at 5 points each, so 25 is
 * the maximum (`OBSERVER_SCORE_MAX`). The weighting and the key list are shared with agents
 * in `constants/agent-score.ts`; only the per-fact weight differs (agents score 7 each).
 *
 * Note the answers live on the DETAIL DTO only — `IObserverApplicationSummary` carries the
 * server's `qualificationScore` and none of the five flags — so list rows cannot compute
 * this and must keep reading the server's figure. Use `observerQualificationScore()` when
 * you may be holding either shape.
 */
export const calculateObserverQualificationScore = (application?: QualificationAnswers | null): number =>
	calculateQualificationScore(application, 'observer');

/** How many of the five facts this application declared (0-5), for a "3 of 5" caption. */
export const countObserverQualifications = (application?: QualificationAnswers | null): number =>
	countQualifications(application);

/**
 * The score to display for an application, and where it came from.
 *
 * Prefers the value computed from the answers whenever they are present: the qualifications
 * checklist is rendered directly beneath the score, so a server figure that disagrees with
 * its own ticks reads as a broken panel. Falls back to the server's `qualificationScore` for
 * a summary row, which carries no answers to compute from.
 */
export const observerQualificationScore = (
	application?: (QualificationAnswers & { qualificationScore?: number | null }) | null
): { score: number; max: number; source: 'computed' | 'server' } => {
	const hasAnswers = !!application && QUALIFICATION_KEYS.some((key) => key in application);
	if (hasAnswers) {
		return { score: calculateObserverQualificationScore(application), max: OBSERVER_SCORE_MAX, source: 'computed' };
	}
	return { score: Number(application?.qualificationScore) || 0, max: OBSERVER_SCORE_MAX, source: 'server' };
};
/* ------------------------------------------------------------------------- *
 * Detail-panel readers.
 *
 * `GET /ObserverApplication/{id}` returns the applicant's fields FLAT — `accountName`,
 * `twitterHandle`, `latitude` — which is also what the create payload sends and what the
 * list DTO carries. The nested `banking` / `socialAccounts` / `preciseLocation` groups are a
 * convenience the mock invented, so the panel must read flat-first and treat the groups as a
 * fallback: reading only the groups renders an empty Banking section against the real server.
 * Every value goes through `cleanObserverField`, because unset entity fields arrive as the
 * literal string "null" and would otherwise print as the word "null".
 * ------------------------------------------------------------------------- */

type ObserverApplicationLike = Partial<IObserverApplicationDetail> & Partial<IObserverApplicationSummary>;

export interface IObserverBanking {
	accountName: string;
	accountNumber: string;
	bankName: string;
}

/** Banking details, or null when the applicant supplied none — so the section can be withheld. */
export const observerBanking = (application?: ObserverApplicationLike | null): IObserverBanking | null => {
	if (!application) return null;
	const banking = {
		accountName: cleanObserverField(application.accountName ?? application.banking?.accountName),
		accountNumber: cleanObserverField(application.accountNumber ?? application.banking?.accountNumber),
		bankName: cleanObserverField(application.bankName ?? application.banking?.bankName),
	};
	return banking.accountName || banking.accountNumber || banking.bankName ? banking : null;
};

export const OBSERVER_SOCIAL_FIELDS = [
	{ key: 'twitterHandle', label: 'X (Twitter)' },
	{ key: 'instagramHandle', label: 'Instagram' },
	{ key: 'facebookHandle', label: 'Facebook' },
] as const;

/** Only the handles the applicant actually gave, so the section shows no empty rows. */
export const observerSocials = (application?: ObserverApplicationLike | null): { label: string; handle: string }[] => {
	if (!application) return [];
	return OBSERVER_SOCIAL_FIELDS.map(({ key, label }) => ({
		label,
		handle: cleanObserverField(application[key] ?? application.socialAccounts?.[key]),
	})).filter((entry) => !!entry.handle);
};

/**
 * The applicant's captured coordinates, or null.
 *
 * `0, 0` is rejected as "not captured" rather than plotted in the Gulf of Guinea — it is the
 * default a device with no fix reports, and no Nigerian polling unit sits there.
 */
export const observerLocation = (
	application?: ObserverApplicationLike | null
): { latitude: number; longitude: number } | null => {
	if (!application) return null;
	const latitude = Number(application.latitude ?? application.preciseLocation?.latitude);
	const longitude = Number(application.longitude ?? application.preciseLocation?.longitude);
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
	if (latitude === 0 && longitude === 0) return null;
	return { latitude, longitude };
};

/**
 * Always an array.
 *
 * `statusHistory` is declared required on the detail DTO but the server does not always send
 * it, and the panel used to call `.map` straight off an optional chain — one absent field away
 * from an uncaught TypeError and the full-page error boundary instead of a detail panel.
 */
export const observerStatusHistory = (application?: ObserverApplicationLike | null): IObserverStatusHistory[] =>
	Array.isArray(application?.statusHistory) ? application.statusHistory : [];

/** The four measured figures one division level arrives as. */
interface IObserverAssignmentStatsLevelLike {
	unassignedCount: number;
	readyToAssignCount: number;
	assignedCount: number;
	totalApplicantCount: number;
}

/**
 * Adapts one wire level into the shape the cards read.
 *
 * `total` and `awaitingApplicants` are computed, not measured — the ledger reports
 * neither. The arithmetic mirrors the agent-side card in
 * `by-electoral-division-ed.tsx`, which reconciles the identical `IAssignmentStatsEntry`
 * against the same four fields, so the two modules cannot drift apart on what
 * "covered" means.
 *
 * `awaitingApplicants` is floored at 0 rather than trusted: it is a subtraction of two
 * independently-reported counts, so a ledger mid-write can transiently report more
 * ready-to-assign than uncovered and render a negative entity count on the card.
 */
export const toObserverCoverageLevel = (entry?: IObserverAssignmentStatsLevelLike | null): IObserverCoverageLevel => {
	const covered = entry?.assignedCount ?? 0;
	const uncovered = entry?.unassignedCount ?? 0;
	const readyToAssign = entry?.readyToAssignCount ?? 0;
	return {
		total: covered + uncovered,
		covered,
		uncovered,
		readyToAssign,
		awaitingApplicants: Math.max(0, uncovered - readyToAssign),
		applicationsReceived: entry?.totalApplicantCount ?? 0,
	};
};

/**
 * Every division level, always — including levels the ledger has not seeded, which come
 * back as an all-zero level rather than a missing key. A card that disappears while the
 * ledger fills would report "not seeded yet" as "nothing to cover".
 */
export const toObserverCoverageSummary = (
	stats?: Partial<Record<keyof IObserverCoverageSummary, IObserverAssignmentStatsLevelLike>> | null
): IObserverCoverageSummary =>
	OBSERVER_DIVISION_TYPES.reduce((summary, division) => {
		summary[division] = toObserverCoverageLevel(stats?.[division]);
		return summary;
	}, {} as IObserverCoverageSummary);

/** True when the ledger has no rows at any level — a different fact from low coverage. */
export const isObserverCoverageEmpty = (summary?: IObserverCoverageSummary | null): boolean =>
	!summary || OBSERVER_DIVISION_TYPES.every((division) => summary[division].total === 0);

/** Wire `resultType` is lowercase (`ec8a`); the app's `ObserverResultType` is upper. */
export const OBSERVER_RESULT_TYPE_PARAM: Record<ObserverDivisionType, string> = {
	state: OBSERVER_DIVISION_TO_RESULT.state.toLowerCase(),
	lga: OBSERVER_DIVISION_TO_RESULT.lga.toLowerCase(),
	ward: OBSERVER_DIVISION_TO_RESULT.ward.toLowerCase(),
	pollingUnit: OBSERVER_DIVISION_TO_RESULT.pollingUnit.toLowerCase(),
};

/** Keys the onboarding link has been seen under, most-specific first. */
const ONBOARDING_LINK_KEYS = ['onboardingLink', 'link', 'url', 'onboardingUrl', 'redirectUrl'];

/**
 * Pulls the onboarding URL out of `GET /ObserverApplication/{id}/onboarding-link`.
 *
 * <p><b>The response shape is not yet known</b> (2026-09-09) — the endpoint is new and the
 * contract has not been published. Rather than guess one shape and silently render a dead button
 * when it turns out to be another, this reads the shapes the service plausibly returns: the
 * Core envelope (`data.data.onboardingLink`), a bare DTO (`data.onboardingLink`), a bare string
 * body, and the same under `link` / `url`. When the contract is confirmed this can collapse to a
 * single read — the tolerance is scaffolding, not a permanent design.</p>
 *
 * <p>Returns null rather than throwing, and the caller must treat null as "no link", NOT as a
 * failure to fetch — a 200 that carries no recognisable link is a real state the UI has to name.</p>
 *
 * <p>Only `http(s)` is accepted. A `javascript:` or `data:` value reaching `window.open` would be
 * script execution from a server response, so the scheme check is a security boundary, not
 * tidiness.</p>
 */
export const readOnboardingLink = (payload: unknown): string | null => {
	const fromValue = (value: unknown): string | null => {
		if (typeof value !== 'string') return null;
		const trimmed = value.trim();
		if (!trimmed) return null;
		try {
			const { protocol } = new URL(trimmed);
			return protocol === 'http:' || protocol === 'https:' ? trimmed : null;
		} catch {
			// Not an absolute URL. A relative path cannot address the other service, so it is
			// not a usable onboarding link either.
			return null;
		}
	};

	const fromObject = (source: unknown): string | null => {
		if (typeof source === 'string') return fromValue(source);
		if (!source || typeof source !== 'object') return null;
		const record = source as Record<string, unknown>;
		for (const key of ONBOARDING_LINK_KEYS) {
			const found = fromValue(record[key]);
			if (found) return found;
		}
		return null;
	};

	if (!payload) return null;
	// The Core envelope first, then the same shapes one level up.
	const enveloped = (payload as { data?: unknown }).data;
	return fromObject(enveloped) ?? fromObject(payload);
};

/**
 * Translates the app's application-list params into the names `/ObserverApplication` binds.
 *
 * <p>Two names had to change and only one of them was obviously wrong. The server binds query
 * params case-insensitively, so `entityCode` already reached `EntityCode` — but `divisionType` is
 * not a name the DTO has under any casing, so a panel scoped to one polling unit was quietly
 * listing every application in the election. Lives here rather than inline in the endpoint so the
 * mapping can be tested without constructing the store.</p>
 *
 * <p>Absent values are OMITTED, never sent empty. `EntityCode=` is a filter on the empty string,
 * which matches nothing — the exact opposite of the unscoped list an undefined code means.</p>
 */
export const observerApplicationParams = ({
	divisionType,
	entityCode,
	qualifications,
	statusId,
	...rest
}: IObserverApplicationListParams): Record<string, unknown> => ({
	...rest,
	// `Status`, not `statusId`: probed on staging (2026-10-19), `Status=4` narrows the list and
	// `statusId` is silently ignored — a status chip sent that way filtered nothing.
	...(statusId != null ? { Status: statusId } : {}),
	...(entityCode ? { EntityCode: entityCode } : {}),
	...(divisionType ? { ResultType: OBSERVER_RESULT_TYPE_PARAM[divisionType] } : {}),
	// One `HasPowerBank=true`-style flag per ticked qualification, the same shape the agent list
	// sends to /Volunteer. Unticked ones are omitted: `=false` would mean "answered NO", which is
	// a narrower list than the "either answer" an unticked box means.
	...Object.fromEntries((qualifications ?? []).map((key) => [key.charAt(0).toUpperCase() + key.slice(1), true])),
});

/**
 * The ledger id that names a row's own entity, by level.
 *
 * Every row carries the whole ancestry, so a ward row still has a `stateId` and an
 * `lgaId`. Reading the wrong one silently labels a ward with its LGA's name.
 */
export const observerEntityId = (row: IObserverLedgerRow, divisionType: ObserverDivisionType): number =>
	divisionType === 'pollingUnit'
		? row.pollingUnitId
		: divisionType === 'ward'
			? row.wardId
			: divisionType === 'lga'
				? row.lgaId
				: row.stateId;

/**
 * The id of the row's PARENT, which is what the name lookups are keyed by — the batch
 * endpoints fetch children of a parent (`getPollingUnitsInWard`, `getWardsInLga`,
 * `getLgasInState`). States have no parent, so they resolve from the flat state list.
 */
export const observerParentId = (row: IObserverLedgerRow, divisionType: ObserverDivisionType): number =>
	divisionType === 'pollingUnit' ? row.wardId : divisionType === 'ward' ? row.lgaId : row.stateId;

/**
 * Adapts one ledger row into a table row.
 *
 * `name` is resolved separately and passed in; it is left EMPTY rather than defaulted to
 * the code, so the table can render the code once and never print a name that is really
 * an id. `fulfillmentState` is deliberately not derived — the ledger cannot distinguish
 * "has applicants, none eligible" from "has eligible applicants", and the table reads the
 * raw booleans anyway.
 */
export const toObserverEntityCoverage = (
	row: IObserverLedgerRow,
	divisionType: ObserverDivisionType,
	name = ''
): IObserverEntityCoverage => ({
	entityId: observerEntityId(row, divisionType) || row.id,
	entityCode: row.code,
	entityName: name,
	divisionType,
	resultType: OBSERVER_DIVISION_TO_RESULT[divisionType],
	applicantCount: row.applicantCount ?? 0,
	hasActiveOffer: !!row.isOfferSent,
	isAssigned: !!row.isAssigned,
	confirmedAt: row.dateAssigned ?? null,
	// Coerced through Number so a wire string cannot reach the hash derivation, and floored at 0
	// rather than left undefined — the ledger's own "nobody assigned" value is 0, and the reports
	// panel already treats that as "no observer to attribute to".
	observerUserId: Number(row.userId) > 0 ? Number(row.userId) : 0,
});

/**
 * When the application was made. The live list rows carry `dateCreated` and NO `appliedAt`, so
 * reading `appliedAt` alone rendered "--" for every applicant.
 */
export const observerAppliedDate = (application: Pick<IObserverApplicationSummary, 'appliedAt' | 'dateCreated'>): string =>
	cleanObserverField(application.appliedAt) || cleanObserverField(application.dateCreated);

export type ObserverApplicationSort = `${'name' | 'score' | 'appliedAt'}-${'asc' | 'desc'}`;

export interface IObserverApplicationCriteria {
	search?: string;
	/** Each ticked fact must have been answered YES. */
	qualifications?: ObserverQualificationKey[];
	/** Absent keeps the incoming order. */
	sort?: ObserverApplicationSort | '';
}

const appliedTime = (application: IObserverApplicationSummary): number => {
	const time = new Date(observerAppliedDate(application)).getTime();
	return Number.isFinite(time) ? time : 0;
};

/**
 * Search, qualification filters and sort, applied client-side.
 *
 * `GET /ObserverApplication` ignored `search`, the qualification flags and `sortBy`/`sortOrder`
 * when probed on staging (2026-10-19); the backend is adding them. A caller that already holds the
 * whole set — one entity's applications — sends the criteria AND filters here, so it works before
 * and after. A PAGED caller must not filter here: one page of many is a slice, not the answer.
 */
export const filterObserverApplications = (
	applications: IObserverApplicationSummary[],
	{ search, qualifications = [], sort }: IObserverApplicationCriteria
): IObserverApplicationSummary[] => {
	const term = (search ?? '').trim().toLowerCase();
	const matches = applications.filter((application) => {
		if (qualifications.some((key) => !isAffirmativeQualification(application[key]))) return false;
		if (!term) return true;
		const haystack = [
			`${cleanObserverField(application.firstName)} ${cleanObserverField(application.lastName)}`,
			cleanObserverField(application.email),
			cleanObserverField(application.phoneNumber),
		]
			.join(' ')
			.toLowerCase();
		return haystack.includes(term);
	});
	if (!sort) return matches;

	const [by, order] = sort.split('-') as ['name' | 'score' | 'appliedAt', 'asc' | 'desc'];
	const direction = order === 'asc' ? 1 : -1;
	const nameOf = (a: IObserverApplicationSummary) =>
		`${cleanObserverField(a.firstName)} ${cleanObserverField(a.lastName)}`.trim().toLowerCase();
	return [...matches].sort((a, b) => {
		const diff =
			by === 'name'
				? nameOf(a).localeCompare(nameOf(b))
				: by === 'score'
					? observerQualificationScore(a).score - observerQualificationScore(b).score
					: appliedTime(a) - appliedTime(b);
		return diff * direction || a.id - b.id;
	});
};
